/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToStores from 'js/lib/connectToStores';
import { useLocation } from 'js/lib/useRouter';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import SimplifiedHeader from 'bundles/composable-page-header-consumer/header-renderers/SimplifiedHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import CourseraPlusHubPageMetatags from 'bundles/coursera-plus-landing-page/components/CourseraPlusHubPageMetatags';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleExperimentProvider, {
  useNavAiSparkle,
} from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';
import { EnrollActionBackground } from 'bundles/unified-description-page-common/utils/EnrollActionBackground';

import 'css!./styl/index';

const GlobalCoachChat = React.lazy(
  () => import('bundles/unified-common/components/coach-on-discovery/GlobalCoachChat')
);

const PAGE_HEADER_CLASSNAME_OVERRIDE = 'page-header-classname-override';

const styles = {
  container: (isMarketing: boolean) => css`
    @media (max-width: 607px) {
      padding-bottom: 52px;
    }

    // This is a hack to override the page header background color for the Coursera Plus landing page.
    .${PAGE_HEADER_CLASSNAME_OVERRIDE} {
      background-color: var(--cds-color-blue-25);
    }

    ${
      isMarketing
        ? css`
            .height-placeholder.with-desktop-header-controls {
              height: 33px;
            }
          `
        : ''
    }
  `,
};

type PropsFromStores = {
  csrfToken: string;
  requestCountryCode: string;
};

const CourseraPlusHubApp = (props: React.PropsWithChildren<PropsFromStores>) => {
  const { children } = props;
  const { pathname } = useLocation();
  const isMarketing = pathname.includes('subscribe') || pathname.includes('special');
  const shouldDisableIndexing = isMarketing;
  const shouldDisablePageHeaderControls = isMarketing;

  const { showNavAiSparkle } = useNavAiSparkle();
  const { isContextProvided, coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = isContextProvided && coachOpen && pageChatType === null;

  return (
    <div className="rc-CourseraPlusHubApp" css={styles.container(isMarketing)}>
      <CourseraPlusHubPageMetatags disableCrawlerIndexing={shouldDisableIndexing} />
      {shouldDisablePageHeaderControls ? (
        <SimplifiedHeader
          showProgramSwitcher={false}
          showLanguageDropdown={false}
          showAcctDropdown={false}
          showSignupBtn={false}
          showLoginBtn={false}
          showMobileHamburger={false}
        />
      ) : (
        <DefaultHeader showMetaNav isPositionStatic />
      )}
      <UniversalPageFeatures />
      <InteractionGuard as="main" preventInteraction={isFullscreenCoachChatOpen}>
        {children}
      </InteractionGuard>
      {showNavAiSparkle && (
        <ErrorBoundaryWithTagging componentName="GlobalCoachChat">
          <React.Suspense fallback={null}>
            <GlobalCoachChat />
          </React.Suspense>
        </ErrorBoundaryWithTagging>
      )}
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        <PageFooter />
      </InteractionGuard>
    </div>
  );
};

const CourseraPlusHubAppWithProviders = (props: React.PropsWithChildren<PropsFromStores>) => {
  const { pathname } = useLocation();
  const isMarketing = pathname.includes('subscribe') || pathname.includes('special');

  if (isMarketing) {
    return <CourseraPlusHubApp {...props} />;
  }

  return (
    <StatsigProvider>
      <GlobalCoachChatStateProvider>
        <NavAiSparkleExperimentProvider>
          <EnrollActionBackground />
          <CourseraPlusHubApp {...props} />
        </NavAiSparkleExperimentProvider>
      </GlobalCoachChatStateProvider>
    </StatsigProvider>
  );
};

export default compose<PropsFromStores, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'premium_hub',
      page: 'coursera_plus_landing_page',
    },
  })),
  connectToStores<PropsFromStores, {}>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  }))
)(CourseraPlusHubAppWithProviders);
